/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

interface TableProps {
  columns: string[];
  columnHeaders: string[];
  data: Array<Record<string, any>>;
  setSelectedOrder?: (value: { id: number; status: string } | null) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  onToggleStatus?: (warehouseId: number) => Promise<void>;
  togglingIds?: { [id: number]: boolean };
}

const WarehouseTableComponent: React.FC<TableProps> = ({
  columns,
  columnHeaders,
  data: initialData,
  setSelectedOrder,
  currentPage,
  setCurrentPage,
  totalPages,
  onToggleStatus,
  togglingIds,
}) => {
  const [tableData, setTableData] = useState(initialData);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingWarehouse, setPendingWarehouse] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    setTableData(initialData);
  }, [initialData]);

  const handleStatusChange = async (id: number, currentStatus: boolean) => {
    if (onToggleStatus) {
      await onToggleStatus(id);
      return;
    }

    const newStatus = !currentStatus;

    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, enabled: newStatus } : row
      )
    );
  };

  const handleOpenDisableConfirm = (id: number, name: string) => {
    setPendingWarehouse({ id, name });
    setConfirmDialogOpen(true);
  };

  const handleCloseDisableConfirm = () => {
    if (pendingWarehouse && togglingIds?.[pendingWarehouse.id]) {
      return;
    }

    setConfirmDialogOpen(false);
    setPendingWarehouse(null);
  };

  const handleConfirmDisable = async () => {
    if (!pendingWarehouse) {
      return;
    }

    await handleStatusChange(pendingWarehouse.id, true);
    setConfirmDialogOpen(false);
    setPendingWarehouse(null);
  };

  // Calculate the correct row number based on items per page (10)
  const getRowNumber = (index: number) => {
    return currentPage * 10 + index + 1;
  };

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-hidden">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              {columnHeaders.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tableData.length > 0 ? (
              tableData.map((row, rowIndex) => {
                const rowNumber = getRowNumber(rowIndex);
                
                return (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {rowNumber}
                    </td>
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className="px-4 py-4 text-sm text-gray-900">
                        {column === "enabled" ? (
                          <div className="flex items-center">
                            <Switch
                              checked={row.enabled}
                              disabled={Boolean(togglingIds?.[row.id])}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (row.enabled) {
                                  handleOpenDisableConfirm(
                                    row.id,
                                    row.name ?? "this warehouse"
                                  );
                                } else {
                                  handleStatusChange(row.id, row.enabled);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              color="primary"
                              size="small"
                              inputProps={{ "aria-label": "status toggle" }}
                            />
                          </div>
                        ) : column === "address" ? (
                          <span>
                            {row.address 
                              ? `${row.address.addressLineOne}, ${row.address.city}, ${row.address.country}`
                              : "N/A"}
                          </span>
                        ) : (
                          <span>
                            {row[column] !== undefined
                              ? row[column]?.toString()
                              : "N/A"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columnHeaders.length + 1}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">No warehouses found</p>
                    <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseDisableConfirm}
        aria-labelledby="disable-warehouse-dialog-title"
        aria-describedby="disable-warehouse-dialog-description"
        maxWidth="xs"
        fullWidth>
        <DialogTitle id="disable-warehouse-dialog-title">
          Disable warehouse?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="disable-warehouse-dialog-description">
            {pendingWarehouse
              ? `Are you sure you want to disable ${pendingWarehouse.name}? This warehouse will be removed from the active list.`
              : "Are you sure you want to disable this warehouse? This warehouse will be removed from the active list."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDisableConfirm}
            disabled={Boolean(
              pendingWarehouse && togglingIds?.[pendingWarehouse.id]
            )}
            color="inherit"
            variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDisable}
            disabled={Boolean(
              pendingWarehouse && togglingIds?.[pendingWarehouse.id]
            )}
            color="error"
            variant="contained">
            {pendingWarehouse && togglingIds?.[pendingWarehouse.id]
              ? "Disabling..."
              : "Yes, Disable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>
            
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Next Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseTableComponent;